import {SearchNameType} from '@aboutyou/legacy-tracking-sdk';
import {useTrackSearch} from '@aboutyou/tracking';
import {useOnClickOutside, useOnTabOutside} from '@aboutyou/ui-components';
import {startTransition, useEffect, useRef, useState} from 'react';

export type onClickSuggestionType = ({
  searchTerm,
  searchType,
}: {
  searchTerm: string;
  searchType: SearchNameType;
}) => void;

const initialSearchState = {
  term: '',
};

export const useSearchBar = ({
  isSearchInTabletMode,
  isSearchBarExpanded,
  collapseSearchBar,
  expandSearchBar,
  searchInputRef,
  searchWrapperRef,
}: {
  isSearchInTabletMode: boolean;
  isSearchBarExpanded: boolean;
  collapseSearchBar?: VoidFunction;
  expandSearchBar?: VoidFunction;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const trackSearch = useTrackSearch();

  const [searchValue, setSearchValue] = useState(initialSearchState.term);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const [suggestionSelected, setSuggestionSelected] = useState(false);

  const handleInputOnChange = (term: string) => {
    setSearchValue(term);
    initialSearchState.term = term;
  };

  useEffect(() => {
    if (isSearchBarExpanded) {
      searchInputRef?.current?.focus({
        preventScroll: true,
      });
    }
  }, [isSearchBarExpanded]);

  const onFocusSearchInput = () => {
    if (!isInputFocused) {
      trackSearch({
        searchAction: 'click',
        searchName: 'searchOpen',
      });
      setIsInputFocused(true);
    }
  };

  const collapseAndBlurInput = (shouldCollapse = true) => {
    if (shouldCollapse) {
      collapseSearchBar?.();
    }

    setSuggestionSelected(false);
    setIsInputFocused(false);
    searchInputRef?.current?.blur();
  };

  const trackSearchClose = () => {
    trackSearch({
      searchAction: 'click',
      searchName: 'searchClose',
    });
  };

  const closeSearchResult = () => {
    if (isInputFocused) {
      // This prevents accidental posting the searchClose-event
      // when the user has clicked on a search result. Input will then be
      // blurred and we have already posted an search event e.g. "suggestedCategory"
      trackSearchClose();
    }
    collapseAndBlurInput();
  };

  const handleRemoveInputFocus = () => {
    if (isInputFocused) {
      collapseAndBlurInput(false);
    }
  };

  const handleOnClickSearchIcon = () => {
    if (isSearchInTabletMode) {
      expandSearchBar?.();
    }
  };

  const handleSetSuggestionSelected = (suggestionsListIndex: number | null) => {
    startTransition(() => {
      setSuggestionSelected(
        Boolean(suggestionsListIndex || suggestionsListIndex === 0),
      );
    });
  };

  useOnClickOutside(searchWrapperRef, closeSearchResult);

  useOnTabOutside(searchWrapperRef, closeSearchResult, {
    trigger: 'onceAndReset',
  });

  return {
    searchBarRef,
    isInputFocused,
    searchValue,
    trackSearchClose,
    onFocusSearchInput,
    handleRemoveInputFocus,
    handleOnClickSearchIcon,
    handleInputOnChange,
    collapseAndBlurInput,
    suggestionSelected,
    setSuggestionSelected: handleSetSuggestionSelected,
  };
};
